import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Text } from '../components/Text';
import { TextArea } from '../components/TextArea';
import { InlineLoadError } from '../components/InlineLoadError';
import { LockIcon } from '../theme/Icons';
import { AlertPresenter } from '../AlertBinder';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { CaremapMemoriesModelFactory } from './CaremapMemoriesModelFactory';
import { MemoriesModel, MemoryScope } from './MemoriesModel';
import { IMemory } from './IMemory';
import { formatMemorySourceLine } from './formatMemorySourceLine';
import { MEMORY_CATEGORY_LABELS } from './memoryCategoryLabels';

const useStyles = createUseStyles({
    categoryPill: {
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 500,
        padding: '2px 10px',
        borderRadius: 999,
        background: 'rgba(var(--ion-color-primary-rgb), 0.15)',
        color: 'var(--ion-color-primary)',
        marginBottom: 14,
    },

    fact: {
        fontSize: 16,
        lineHeight: '1.6',
        color: 'var(--ion-text-color)',
        marginBottom: 10,
    },

    sourceLine: {
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        marginBottom: 14,
    },

    lockNote: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        marginBottom: 14,
    },

    actionsBar: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 8,
        marginTop: 10,

        '& ion-button': {
            flex: '1 1 auto',
        },
    },

    emptyState: {
        padding: 20,
    },
});

interface IMemoryEditFormProps {
    draftFact: string;
    isSaving: boolean;
    onDraftChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
}

const MemoryEditForm: FC<IMemoryEditFormProps> = ({ draftFact, isSaving, onDraftChange, onSave, onCancel }) => {
    const classes = useStyles();

    return (
        <>
            <TextArea
                data-id="memory-edit-input"
                value={draftFact}
                rows={4}
                autoGrow
                onInput={onDraftChange}
            />
            <div className={classes.actionsBar}>
                <IonButton
                    color="primary"
                    onClick={onSave}
                    disabled={isSaving || !draftFact.trim()}
                    data-id="memory-save-button"
                >
                    Save
                </IonButton>
                <IonButton
                    color="medium"
                    fill="clear"
                    onClick={onCancel}
                    disabled={isSaving}
                    data-id="memory-cancel-edit-button"
                >
                    Cancel
                </IonButton>
            </div>
        </>
    );
};

interface IMemoryViewDetailProps {
    memory: IMemory;
    scope: MemoryScope;
    onEdit: () => void;
    onDelete: () => void;
}

const MemoryViewDetail: FC<IMemoryViewDetailProps> = ({ memory, scope, onEdit, onDelete }) => {
    const classes = useStyles();

    return (
        <>
            <div className={`${classes.fact} mp-mask`} data-id="memory-fact">{memory.fact}</div>
            <div className={classes.sourceLine}>{formatMemorySourceLine(memory)}</div>

            {scope === 'personal' && (
                <div className={classes.lockNote} data-id="memory-drawer-personal-lock">
                    <LockIcon color="medium" size="small" />
                    <span>Only you can see this</span>
                </div>
            )}

            <div className={classes.actionsBar}>
                <IonButton
                    color="primary"
                    fill="outline"
                    onClick={onEdit}
                    data-id="memory-edit-button"
                >
                    Edit
                </IonButton>
                <IonButton
                    color="danger"
                    fill="clear"
                    onClick={onDelete}
                    data-id="memory-delete-button"
                >
                    Delete
                </IonButton>
            </div>
        </>
    );
};

// Isolates the edit-mode form state and its Save call from MemoryDetail's render/delete concerns.
// editMemory adopts the PUT response's new identity server-side, so on success this reports the
// superseded memoryId back via onEdited -- letting the parent keep the panel pointed at the
// replacement row instead of the now-dead old id.
const useMemoryEdit = (params: { model: MemoriesModel; memory: IMemory; onEdited: (memoryId: string) => void }) => {
    const { model, memory, onEdited } = params;
    const alert = useInstance(AlertPresenter);

    const [isEditing, setIsEditing] = useState(false);
    const [draftFact, setDraftFact] = useState(memory.fact);
    const [isSaving, setIsSaving] = useState(false);

    const startEditing = () => {
        setDraftFact(memory.fact);
        setIsEditing(true);
    };

    const saveEdit = async () => {
        const fact = draftFact.trim();
        if (!fact || fact === memory.fact) {
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        try {
            const superseded = await model.editMemory(memory, fact);
            if (superseded) {
                onEdited(superseded.memoryId);
            }
            setIsEditing(false);
        } catch (err: any) {
            alert.showAndLogError(err);
        } finally {
            setIsSaving(false);
        }
    };

    return { isEditing, draftFact, isSaving, startEditing, saveEdit, setDraftFact, cancelEditing: () => setIsEditing(false) };
};

interface IMemoryDetailProps {
    memory: IMemory;
    scope: MemoryScope;
    model: MemoriesModel;
    onEdited: (memoryId: string) => void;
    onClose: () => void;
}

const MemoryDetail: FC<IMemoryDetailProps> = ({ memory, scope, model, onEdited, onClose }) => {
    const classes = useStyles();
    const alert = useInstance(AlertPresenter);

    const { isEditing, draftFact, isSaving, startEditing, saveEdit, setDraftFact, cancelEditing } = useMemoryEdit({ model, memory, onEdited });

    const onDelete = async () => {
        const destroyOrCancel = await alert.confirmDestructiveAction({
            header: 'Delete this memory?',
            message: 'Bea will no longer use this to help support you, and it can\'t be recovered.',
        });

        if (destroyOrCancel !== 'destroy') {
            return;
        }

        try {
            await model.deleteMemory(memory);
            onClose();
        } catch (err: any) {
            alert.showAndLogError(err);
        }
    };

    return (
        <div data-id="memory-drawer" data-type="memory-drawer-content" data-memory-id={memory.memoryId}>
            <div className={classes.categoryPill}>{MEMORY_CATEGORY_LABELS[memory.category]}</div>

            {isEditing ? (
                <MemoryEditForm
                    draftFact={draftFact}
                    isSaving={isSaving}
                    onDraftChange={setDraftFact}
                    onSave={saveEdit}
                    onCancel={cancelEditing}
                />
            ) : (
                <MemoryViewDetail memory={memory} scope={scope} onEdit={startEditing} onDelete={onDelete} />
            )}
        </div>
    );
};

export interface IMemoryDetailPanelProps {
    memoryId: string;
    scope: MemoryScope;

    // Called when the detail should be dismissed -- the drawer closes itself, the in-modal host
    // returns to its list.
    onClose: () => void;

    // A host that encodes the memoryId in its URL (the query-param drawer) passes this so it can
    // repoint that URL after an edit supersedes the row; hosts without a URL (the in-modal Account
    // Settings view) omit it and rely on the internal displayedMemoryId alone.
    onSupersededByEdit?: (memoryId: string) => void;
}

// The presentation-agnostic memory detail: loads the scope's model, resolves the row, and renders
// view/edit/delete. Hosted both by the standalone query-param drawer and inline inside the Account
// Settings modal, so it takes close/edit-supersede as callbacks rather than reaching for the drawer.
export const MemoryDetailPanel: FC<IMemoryDetailPanelProps> = ({ memoryId, scope, onClose, onSupersededByEdit }) => {
    const classes = useStyles();
    const factory = useInstance(CaremapMemoriesModelFactory);

    const model = scope === 'personal' ? factory.forPersonal() : factory.forActiveCaremap();
    useSubscription(() => model);

    useAsyncEffect(async () => {
        await model.ensureLoaded();
    }, [model]);

    // Editing a memory supersedes it under a brand-new memoryId (MemoriesModel.editMemory adopts the
    // PUT response's identity), so the id we opened with goes stale the moment a save succeeds. Track
    // the id actually being displayed so a successful edit keeps this panel on its replacement row
    // instead of falling through to the "no longer available" state below.
    const [displayedMemoryId, setDisplayedMemoryId] = useState(memoryId);

    const onEdited = (newMemoryId: string) => {
        setDisplayedMemoryId(newMemoryId);
        onSupersededByEdit?.(newMemoryId);
    };

    const memory = model.getMemories().find(candidate => candidate.memoryId === displayedMemoryId);

    if (model.loadFailed) {
        return (
            <div className={classes.emptyState} data-id="memory-drawer" data-type="memory-drawer-error">
                <InlineLoadError
                    dataId="memory-drawer-load-error"
                    message="Couldn't load this memory. Check your connection and try again."
                    onRetry={() => model.ensureLoaded()}
                />
            </div>
        );
    }

    // Guard on hasLoaded so a fresh deep-link (list not fetched yet, so `memory` is legitimately
    // undefined) doesn't flash the "no longer available" state before the fetch has a chance to
    // resolve (the note views make the same distinction, via useResolvedNote).
    if (!model.hasLoaded) {
        return null;
    }

    if (!memory) {
        return (
            <div className={classes.emptyState} data-id="memory-drawer" data-type="memory-drawer-empty">
                <Text>This memory is no longer available. It may have been removed.</Text>
            </div>
        );
    }

    return (
        <MemoryDetail
            memory={memory}
            scope={scope}
            model={model}
            onEdited={onEdited}
            onClose={onClose}
        />
    );
};
